/**
 * ZodiacRing - Central disc showing the 12 zodiac signs
 * 
 * CiH Phase Mapping (Seasonal/Wheel-based):
 * NOURISH: Capricorn, Aquarius, Pisces (Winter signs)
 * ACTIVATE: Aries, Taurus, Gemini (Spring signs)
 * FULFILL: Cancer, Leo, Virgo (Summer signs)
 * INTEGRATE: Libra, Scorpio, Sagittarius (Autumn signs)
 * 
 * Regional Adaptation:
 * For Southern Hemisphere and Equatorial zones, the entire zodiac ring is rotated 180°
 * This reflects the "view from below the equator" perspective
 * while keeping the astronomical/seasonal wheel consistent.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { ZODIAC_SIGNS, createArcPath, createSegmentPath, svgDegreesToRadians } from './clockUtils';
import { getCihPhaseColor, getZodiacCihPhase } from '../utils/styleCard';

const ZodiacRing = ({ center, radius, currentSign, onZodiacClick, isSouthernHemisphere = false, isEquatorialZone = false }) => {
  // Extended inward for more planetary space
  const innerRadius = radius - 90;  // Smaller inner circle for more space
  const glyphRadius = radius - 65;   // Glyphs towards center (swapped)
  const labelRadius = radius - 20;   // Labels on outside (swapped)
  const pathIdPrefix = `zodiac-path-${center}`;
  
  // Apply 180° rotation only for Southern TEMPERATE Hemisphere
  // Equatorial zones show true zodiac positions without offset
  const hemisphereOffset = (isSouthernHemisphere && !isEquatorialZone) ? 180 : 0;

  return (
    <motion.g initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2, duration: 0.8 }} data-testid="zodiac-ring">
      <defs>
        <radialGradient id="bronzeDiscGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4A574" />
          <stop offset="40%" stopColor="#C5A059" />
          <stop offset="70%" stopColor="#B8860B" />
          <stop offset="100%" stopColor="#8B6914" />
        </radialGradient>
        {ZODIAC_SIGNS.map((sign, index) => {
          // Each sign spans 30° counter-clockwise from its starting angle
          // Apply hemisphere offset for Southern view
          const startAngle = (sign.angle + hemisphereOffset) % 360;
          const endAngle = startAngle + 30; // Counter-clockwise progression (adding in SVG coords)
          return (
            <path key={`${pathIdPrefix}-${index}`} id={`${pathIdPrefix}-${index}`}
              d={createArcPath(center, labelRadius, startAngle, endAngle)} fill="none" />
          );
        })}
      </defs>

      <circle cx={center} cy={center} r={radius} fill="url(#bronzeDiscGradient)" stroke="#8B6914" strokeWidth="3" />
      <circle cx={center} cy={center} r={innerRadius} fill="#0B0F19" stroke="#A08040" strokeWidth="2" />

      {ZODIAC_SIGNS.map((sign, index) => {
        // Each sign spans 30° from its starting angle
        // Apply hemisphere offset for Southern view
        const startAngle = (sign.angle + hemisphereOffset) % 360;
        const endAngle = startAngle + 30;
        
        const { path, divisionLine } = createSegmentPath(center, innerRadius, radius, startAngle, endAngle);
        
        // Calculate glyph position at midpoint of segment
        const midRad = svgDegreesToRadians(startAngle + 15);
        const glyphX = center + Math.cos(midRad) * glyphRadius;
        const glyphY = center + Math.sin(midRad) * glyphRadius;
        
        // Get CiH phase color for this sign
        const cihPhase = getZodiacCihPhase(sign.name);
        const cihColor = getCihPhaseColor(cihPhase);
        
        // Check if this is the current Sun sign
        const isCurrentSign = currentSign && sign.name.toLowerCase() === currentSign.toLowerCase();
        
        // Styling based on current sign status
        const segmentOpacity = isCurrentSign ? 0.25 : (index % 2 === 0 ? 0.08 : 0.04);
        const glyphColor = isCurrentSign ? '#FFFFFF' : '#3D2B1F';
        const labelColor = isCurrentSign ? '#FFFFFF' : '#4A3728';
        const glyphSize = isCurrentSign ? '42' : '36';
        const labelSize = isCurrentSign ? '19' : '16';

        return (
          <g key={sign.name}>
            {/* Segment with CiH phase tint - brighter for current sign */}
            <motion.path d={path} 
              fill={cihColor}
              opacity={segmentOpacity}
              stroke={isCurrentSign ? cihColor : '#6B5510'} 
              strokeWidth={isCurrentSign ? 2 : 1} 
              style={{ cursor: 'pointer', shapeRendering: 'geometricPrecision' }}
              whileHover={{ opacity: 0.3, fill: cihColor }}
              onClick={() => onZodiacClick?.('zodiac', sign.name)}
              data-testid={`zodiac-section-${sign.name.toLowerCase()}`} />
            
            {/* Division line */}
            <line {...divisionLine} stroke="#6B5510" strokeWidth="1.5" style={{ shapeRendering: 'crispEdges' }} />
            
            {/* CiH glow for current sign */}
            {isCurrentSign && (
              <motion.path
                d={path}
                fill="none"
                stroke={cihColor}
                strokeWidth={3}
                opacity={0.6}
                style={{ shapeRendering: 'geometricPrecision' }}
                animate={{ opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
            
            {/* Glyph - larger and white for current sign */}
            <text x={glyphX} y={glyphY} textAnchor="middle" dominantBaseline="central"
              fill={glyphColor} fontSize={glyphSize} fontFamily="serif" fontWeight="bold" 
              style={{ 
                pointerEvents: 'none', 
                textRendering: 'optimizeLegibility',
                textShadow: isCurrentSign ? `0 0 8px ${cihColor}` : 'none'
              }}>
              {sign.glyph}
            </text>
            
            {/* Sign name along arc - bold and white for current sign */}
            <text 
              fill={labelColor} 
              fontSize={labelSize} 
              fontFamily="serif" 
              fontWeight={isCurrentSign ? 'bold' : '500'}
              style={{ 
                pointerEvents: 'none', 
                textTransform: 'uppercase', 
                letterSpacing: '1.5px', 
                textRendering: 'optimizeLegibility',
                textShadow: isCurrentSign ? `0 0 6px ${cihColor}` : 'none'
              }}>
              <textPath href={`#${pathIdPrefix}-${index}`} startOffset="50%" textAnchor="middle">
                {sign.name}
              </textPath>
            </text>
          </g>
        );
      })}
      
      <circle cx={center} cy={center} r={radius - 1} fill="none" stroke="#A08040" strokeWidth="1" opacity="0.6" />
    </motion.g>
  );
};

export default ZodiacRing;
